import { stumbleOnGuardMiss } from '@ipfabric/frontend-shared-assets/utils/stumble/stumbleOnGuardMiss';
import { isReportSeverityKey } from '@ipfabric/utils/report';
import classNames from 'classnames';
import type { FC, KeyboardEvent } from 'react';
import { useMemo, useState } from 'react';
import { useClickAway } from '../hooks/useClickAway';
import {
  ARROW_DOWN,
  ARROW_UP,
  TAB,
  isEnterOrSpace,
} from '../utils/keyboard/keyboardKeys';
import { IntentFilterButton } from './IntentFilterButton';
import { IntentFilterDropdown } from './IntentFilterDropdown';
import { severityDots } from './IntentFilterSelect.styles';
import type { IntentFilterSelectProps } from './IntentFilterSelect.types';

const noValue = (
  <div className='grid grid-cols-2 grid-rows-2 gap-0.5 size-4 items-stretch'>
    {severityDots['0']}
    {severityDots['10']}
    {severityDots['20']}
    {severityDots['30']}
  </div>
);

export const IntentFilterSelect: FC<IntentFilterSelectProps> = (props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [focused, setFocused] = useState<number | null>(null);
  const options = useMemo(
    () =>
      Object.keys(props.options).filter(
        stumbleOnGuardMiss(isReportSeverityKey),
      ),
    [props.options],
  );

  const ref = useClickAway<HTMLDivElement>(() => {
    setOpen(false);
  });

  const handleEnterOrSpaceKey = () => {
    if (focused === null) {
      return;
    }

    if (focused === options.length) {
      props.onChange(null);
    } else {
      const focusedOption = options[focused];
      if (focusedOption === undefined) {
        return;
      }

      props.onChange(focusedOption);
    }

    setFocused(null);
  };

  const handleArrowDownOrTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (focused === null) {
      setFocused(0);
    } else {
      // if something is focused, then the cancel button is displayed on the last position
      const cancelButtonAdjustment = props.value === null ? 1 : 0;
      setFocused(
        Math.min(focused + 1, options.length - cancelButtonAdjustment),
      );
    }
  };

  const handleArrowUpOrShiftTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (focused !== null) {
      const next = focused - 1;
      setFocused(next < 0 ? null : next);
    }
  };

  const KEY_HANDLERS = {
    SELECTION: {
      matches: (e: KeyboardEvent): boolean => isEnterOrSpace(e.key),
      handler: handleEnterOrSpaceKey,
      requiresOpen: false,
    },
    NEXT_ITEM: {
      matches: (e: KeyboardEvent): boolean =>
        e.key === ARROW_DOWN || (e.key === TAB && !e.shiftKey),
      handler: handleArrowDownOrTabKey,
      requiresOpen: true,
    },
    PREVIOUS_ITEM: {
      matches: (e: KeyboardEvent): boolean =>
        e.key === ARROW_UP || (e.key === TAB && e.shiftKey),
      handler: handleArrowUpOrShiftTabKey,
      requiresOpen: true,
    },
  } as const;

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    for (const { matches, handler, requiresOpen } of Object.values(
      KEY_HANDLERS,
    )) {
      if (matches(e) && (!requiresOpen || open)) {
        handler(e);
      }
    }
  };

  return (
    <div className={classNames('relative', props.className)} ref={ref}>
      <IntentFilterButton
        onClick={() => {
          setOpen((prev) => !prev);
          setFocused(null);
        }}
        onKeyDown={handleKeyDown}
        open={open}
      >
        {props.value === null ? noValue : severityDots[props.value]}
      </IntentFilterButton>
      {open && (
        <IntentFilterDropdown
          setOpen={setOpen}
          options={props.options}
          onChange={props.onChange}
          value={props.value}
          focused={focused}
        />
      )}
    </div>
  );
};
